/**
 * OpenAI モデルコスト計算
 *
 * `getOrganizationUsageDetails` の per-result コスト計算を、共有状態に依存しない
 * 純関数として切り出したもの。service tier ごとの料金表選択・画像/音声/トークン
 * 課金の分岐を集約する。
 */
import { modelPricing, priorityTierPricing, audioTranscriptionPerSecondPricing } from './pricing';
import { isOpenAiWebRunUsage } from './web-search';
import { isOpenAiGptLiveModel } from './gpt-live';

export interface ModelCostInput {
  model: string;
  usageType: string;
  serviceTier: string;
  /** Usage API の result.batch（group_by=batch 時のみ存在）。Batch API 利用は 50% 割引 */
  batch?: boolean;
  contextTokens: number;
  generatedTokens: number;
  cachedTokens: number;
  /** GPT-5.6+ prompt tokens written to cache (billed at 1.25x input when surfaced). */
  cacheWriteTokens: number;
  requests: number;
  imageCount: number;
  audioCharacters: number;
  audioSeconds: number;
}

export interface ModelCostResult {
  /** 計算されたコスト（料金表が無い場合は 0） */
  cost: number;
  /** 料金表が見つかったか。false の場合、呼び出し側は集計をスキップし警告する */
  pricingFound: boolean;
}

/**
 * 1 件の usage result に対するコストを計算する。
 * 料金表が見つからない場合は cost=0 / pricingFound=false を返し、集計の可否は
 * 呼び出し側に委ねる（元の実装の「pricing が無ければ加算しない」挙動を保持）。
 */
export const calculateModelCost = ({
  model,
  usageType,
  serviceTier,
  batch,
  contextTokens,
  generatedTokens,
  cachedTokens,
  cacheWriteTokens,
  requests,
  imageCount,
  audioCharacters,
  audioSeconds,
}: ModelCostInput): ModelCostResult => {
  // `webrun` is an internal marker for a web-search run, not a token-priced
  // model. The separate Costs API line item is reconciled after usage folding.
  if (isOpenAiWebRunUsage(model, usageType)) {
    return { cost: 0, pricingFound: true };
  }

  // GPT-Live 1 sessions are duration-priced and reconciled from the Costs API.
  // Backend model/tool usage remains separate, so completion tokens must not be
  // interpreted as the live-session charge.
  if (isOpenAiGptLiveModel(model)) {
    return { cost: 0, pricingFound: true };
  }

  // コスト計算 - Select pricing table based on service tier
  // Fast mode also accepts the former priority name; retain the legacy alias.
  const isPriorityTier = serviceTier === 'fast' || serviceTier === 'priority' || serviceTier === 'priority-tier';
  let pricing;
  if (isPriorityTier) {
    // Try priority tier pricing first, fallback to default if not found
    pricing = priorityTierPricing[model as keyof typeof priorityTierPricing] ||
             modelPricing[model as keyof typeof modelPricing];
  } else {
    pricing = modelPricing[model as keyof typeof modelPricing];
  }
  // Flex tier: 50% discount on standard pricing
  // Batch API (result.batch === true): 50% discount
  // Scale tier: treated as standard (pricing not publicly documented)
  const tierCostMultiplier = (serviceTier === 'flex' ? 0.5 : 1.0) * (batch === true ? 0.5 : 1.0);
  let modelCost = 0;
  if (pricing) {
    // dall-e のみ画像/リクエスト単価。gpt-image-1 系（1 / 1.5 / mini）はトークン課金で、
    // images usage API も input_tokens / output_tokens を返す
    const isPerImageModel = model.startsWith('dall-e');

    if (usageType === 'images' && isPerImageModel) {
      modelCost = imageCount * pricing.input;
    } else if (usageType === 'audio_speeches') {
      modelCost = audioCharacters * pricing.input;
    } else if (usageType === 'audio_transcriptions') {
      // 利用量は秒で返るため、トークン単価ではなく秒単価で計算する
      const perSecondPrice = audioTranscriptionPerSecondPricing[model];
      modelCost = audioSeconds * (perSecondPrice ?? pricing.input);
    } else if (isPerImageModel) {
      // dall-e usage surfacing in completions data - per request pricing
      modelCost = requests * pricing.input;
    } else {
      // Calculate cost with cached tokens support for text/token-based models
      const cachedInputPrice = pricing.cachedInput ?? pricing.input;
      const cacheWritePrice = pricing.cacheWrite ?? pricing.input;
      const cachedCost = cachedTokens * cachedInputPrice;
      const cacheWriteCost = cacheWriteTokens * cacheWritePrice;
      const regularInputCost = Math.max(0, contextTokens - cachedTokens - cacheWriteTokens) * pricing.input;
      const outputCost = generatedTokens * pricing.output;
      modelCost = cachedCost + cacheWriteCost + regularInputCost + outputCost;
    }
    modelCost *= tierCostMultiplier;
    return { cost: modelCost, pricingFound: true };
  }

  return { cost: modelCost, pricingFound: false };
};
